import React, { useCallback, useRef } from 'react';
import PaintBucketIcon from '@atlaskit/icon/core/paint-bucket';
import EditorBackgroundColorIcon from '@atlaskit/icon/glyph/editor/background-color';
import { SpotlightTarget, SpotlightManager } from '@atlaskit/onboarding';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { ExperienceFailed } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { ThemedButton } from '@atlassian/jira-business-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { LIST_FORMAT_RULE_EXPERIENCE } from '../../common/constants';
import { useFormatRulesSerialisedJql } from '../../controllers/format-rules-store';
import { useFormatRulesSidebar } from '../../controllers/sidebar';
import messages from './messages';
import { formatRulesEntryPoint } from './sidebar/entrypoint';
import { SidebarFallback } from './sidebar/fallback';
import { Onboarding } from './sidebar/onboarding';

export const FormatButton = () => {
	const cloudId = useCloudId();
	const projectData = useProject();
	const { formatMessage } = useIntl();
	const [hydrateJql] = useFormatRulesSerialisedJql();
	const formatButtonRef = useRef<HTMLButtonElement>();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(formatRulesEntryPoint, {
		projectKey: projectData.key,
		cloudId,
		hydrateJql,
	});

	const buttonTriggerRef = useEntryPointButtonTrigger(entryPointActions);

	const errorFallback = useCallback(
		({ error }: { error: Error }) => (
			// TODO: show error flag
			<ExperienceFailed experience={LIST_FORMAT_RULE_EXPERIENCE} error={error} />
		),
		[],
	);

	const { onClickFormatButton, isFormatRulesSidebarOpen } = useFormatRulesSidebar({
		SidebarComponent: (props) => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="jwm.business.format.rules"
				packageName="jiraBusinessFormatRules"
				runtimeProps={props}
				fallback={<SidebarFallback />}
				errorFallback={errorFallback}
			/>
		),
		projectId: String(projectData.id),
		formatButtonRef,
	});

	return (
		<SpotlightManager>
			<SpotlightTarget name="format-rule-button">
				<ThemedButton
					onClick={() => onClickFormatButton({ showOnboarding: false })}
					iconBefore={
						isVisualRefreshEnabled() ? (
							<PaintBucketIcon label="" color="currentColor" />
						) : (
							<EditorBackgroundColorIcon label="" size="medium" />
						)
					}
					isSelected={isFormatRulesSidebarOpen}
					appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
					ref={mergeRefs(buttonTriggerRef, formatButtonRef)}
					testId="business-conditional-formatting-sidebar.ui.format-rules-sidebar.themed-button"
				>
					{formatMessage(messages.format)}
				</ThemedButton>
			</SpotlightTarget>
			<Onboarding
				ref={mergeRefs(buttonTriggerRef, formatButtonRef)}
				onClickFormatButton={onClickFormatButton}
			/>
		</SpotlightManager>
	);
};
